import React, { useState, useRef } from 'react';
import { Paperclip, X, FileImage } from 'lucide-react';
import { Card, CardContent, CardHeader } from './ui/Card';
import { Label } from './ui/Label';
import { SectionHeader } from './ui/SectionHeader';
import { MedicalFormData } from '../hooks/useFormState';
interface AttachmentsProps {
  data: MedicalFormData;
  onChange: (field: keyof MedicalFormData, value: any) => void;
}
export function Attachments({
  data,
  onChange
}: AttachmentsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imagePreviews, setImagePreviews] = useState<{
    [key: number]: string;
  }>({});
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newFiles = Array.from(files).filter(file => {
        const isImage = file.type.startsWith('image/');
        return isImage;
      });
      // Create preview URLs for new images
      const currentIndex = data.attachments.length;
      newFiles.forEach((file, index) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => ({
            ...prev,
            [currentIndex + index]: reader.result as string
          }));
        };
        reader.readAsDataURL(file);
      });
      onChange('attachments', [...data.attachments, ...newFiles]);
    }
  };
  const handleRemoveFile = (index: number) => {
    const updatedFiles = data.attachments.filter((_, i) => i !== index);
    // Remove preview for deleted image
    setImagePreviews(prev => {
      const newPreviews = {
        ...prev
      };
      delete newPreviews[index];
      // Reindex remaining previews
      const reindexed: {
        [key: number]: string;
      } = {};
      Object.keys(newPreviews).forEach(key => {
        const oldIndex = parseInt(key);
        if (oldIndex > index) {
          reindexed[oldIndex - 1] = newPreviews[oldIndex];
        } else {
          reindexed[oldIndex] = newPreviews[oldIndex];
        }
      });
      return reindexed;
    });
    onChange('attachments', updatedFiles);
  };
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };
  return <Card className="h-full" data-id="element-1">
      <CardHeader data-id="element-2">
        <SectionHeader title="Medical Attachments" subtitle="Upload medical images and reports" icon={<Paperclip className="w-5 h-5" data-id="element-4" />} data-id="element-3" />
      </CardHeader>
      <CardContent className="space-y-6" data-id="element-5">
        {/* Upload Area */}
        <div data-id="element-6">
          <Label className="text-base font-semibold text-amber-900 mb-3 block" data-id="element-7">
            Upload Images Only
          </Label>

          <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-amber-300 rounded-xl p-8 text-center cursor-pointer hover:border-amber-500 hover:bg-amber-50/50 transition-all" data-id="element-8">
            <Paperclip className="w-12 h-12 text-amber-600 mx-auto mb-3" data-id="element-9" />
            <p className="text-stone-700 font-medium mb-2" data-id="element-10">
              Click to upload or drag and drop
            </p>
            <p className="text-sm text-stone-500" data-id="element-11">
              Supported formats: JPG, PNG, GIF (Max 10MB per file)
            </p>
          </div>

          <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handleFileSelect} className="hidden" data-id="element-12" />
        </div>

        {/* Uploaded Files List with Previews */}
        {data.attachments.length > 0 && <div data-id="element-13">
            <Label className="text-base font-semibold text-amber-900 mb-3 block" data-id="element-14">
              Uploaded Images ({data.attachments.length})
            </Label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-15">
              {data.attachments.map((file, index) => <div key={index} className="relative bg-amber-50 border-2 border-amber-200 rounded-lg overflow-hidden" data-id="element-16">
                  {/* Image Preview */}
                  {imagePreviews[index] && <div className="w-full h-48 bg-stone-100" data-id="element-17">
                      <img src={imagePreviews[index]} alt={file.name} className="w-full h-full object-contain" data-id="element-18" />
                    </div>}

                  {/* File Info */}
                  <div className="p-4 flex items-center gap-3" data-id="element-19">
                    <div className="flex-shrink-0" data-id="element-20">
                      <FileImage className="w-6 h-6 text-amber-600" data-id="element-21" />
                    </div>

                    <div className="flex-1 min-w-0" data-id="element-22">
                      <p className="text-sm font-medium text-stone-800 truncate" data-id="element-23">
                        {file.name}
                      </p>
                      <p className="text-xs text-stone-500" data-id="element-24">
                        {formatFileSize(file.size)}
                      </p>
                    </div>

                    {/* Remove Button */}
                    <button type="button" onClick={() => handleRemoveFile(index)} className="flex-shrink-0 p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors print:hidden" data-id="element-25">
                      <X className="w-5 h-5" data-id="element-26" />
                    </button>
                  </div>
                </div>)}
            </div>
          </div>}

        {/* Info Note */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4" data-id="element-27">
          <p className="text-sm text-blue-800" data-id="element-28">
            <span className="font-semibold" data-id="element-29">Note:</span> Please upload relevant
            medical images, X-rays, or scan reports. Send PDF documents
            separately. All files are securely stored and kept confidential.
          </p>
        </div>
      </CardContent>
    </Card>;
}